import { computed, type ComputedRef } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { logger } from '@tools/logging';
import {
  getApiV1UserClaims,
  getGetApiV1UserClaimsQueryOptions,
  useGetApiV1UserClaims,
} from '@services/identity-api/orval-api/user-api/user-api';
import { type FlattenedPermissions } from './types';

export function hasPermission(permission: FlattenedPermissions): ComputedRef<boolean | undefined> {
  try {
    const { isFetching, data: claims } = useGetApiV1UserClaims({
      query: {
        staleTime: 60 * 1000,
        retry: 10,
      },
    });

    return computed(() => {
      if (isFetching.value || claims.value === undefined) {
        return undefined;
      }

      return claims.value.some((t) => t.type === 'Permission' && t.value === permission);
    });
  } catch (error) {
    if (
      getErrorMessage(error).startsWith('vue-query hooks can only be used inside setup() function')
    ) {
      logger.error(
        new Error(
          'hasPermission() is aangeroepen buiten een setup() functie of effect scope. Dit moet aangepast worden naar hasPermissionAwaitable().',
        ),
      );
    }

    throw error;
  }
}

export async function hasPermissionAwaitable(
  permission: FlattenedPermissions,
): Promise<boolean | undefined> {
  try {
    const client = useQueryClient();
    const claims = await client.ensureQueryData(
      getGetApiV1UserClaimsQueryOptions({ query: { staleTime: 60 * 1000, retry: 10 } }),
    );
    return claims.some((claim) => claim.type === 'Permission' && claim.value === permission);
  } catch (error: any) {
    if (
      getErrorMessage(error).startsWith('vue-query hooks can only be used inside setup() function')
    ) {
      const claims = await getApiV1UserClaims();
      return claims.some((claim) => claim.type === 'Permission' && claim.value === permission);
    }
    throw error;
  }
}

function getErrorMessage(error: any): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error === undefined || error === null) return 'Onbekende fout (undefined/null)';

  let errorMessage = 'Onbekende fout';
  const errorPrototype = Object.getPrototypeOf(error);
  if (typeof errorPrototype.toString === 'function') {
    const output = errorPrototype.toString.apply(error);
    if (typeof output === 'string') {
      errorMessage = output;
    }
  }
  return errorMessage;
}
